import { useTranslation } from 'react-i18next';
import { Scale } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import { useShowServingSize } from '@/hooks/useShowServingSize';
import { cn } from '@/lib/utils';

/**
 * Toggle for the serving-size column on each meal item / add-on in the
 * food-log timeline (e.g. "2.5×60g (serving)"). ON by default; saved
 * per-device like the theme / font / thumbnail prefs.
 */
export const ServingSizeToggle = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [show, setShow] = useShowServingSize();

  const toggle = (
    <button
      type="button"
      role="switch"
      aria-checked={show}
      aria-label={t('Show serving sizes')}
      onClick={() => setShow(!show)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        show ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          show ? 'translate-x-[1.375rem]' : 'translate-x-0.5',
        )}
      />
    </button>
  );

  return (
    <PrefSection
      variant={variant}
      icon={Scale}
      title={t('Show serving sizes')}
      description={t(
        'Show each food’s serving size in the food log, e.g. “2.5×60g (serving)”. Saved to this browser.',
      )}
      action={toggle}
    />
  );
};
